<template>
  <div class="price-breakdown">
    <div class="callout callout-info special-rates" v-if="hasOvernight || hasWeekend">
      <strong>This car has special discount rates!</strong><br/><br/>
      <p v-if="hasWeekend">
        <strong><span class="fa fa-star"></span> Weekend Rates</strong><br/>
        The discounted weekend rate kicks in on Friday at
        {{ weekendRateStart | time_format }} and ends on Monday at
        {{ weekendRateEnd | time_format }} and includes {{ object.rate.includeKms * 2}}kms! Take the car
        for the entire weekend from just {{ weekendRate | currency }}!
      </p>
      <p v-if="hasOvernight">
        <strong><span class="fa fa-moon-o"></span> Overnight Rates</strong><br/>
        Simply book the car for 6 or more hours on or after
        {{ overnightRateStart | time_format }} and return it at any
        time up to {{ overnightRateEnd | time_format }} the next
        morning from just {{ overnightRate | currency }}!
      </p>
    </div>
    <h4>Price Breakdown:</h4>
    <div class="alert alert-info">
      Prices are automatically calculated at their lowest possible rate
    </div>
    <p>
      Booking from <strong>{{ startTime }}</strong> to
      <strong>{{ endTime }}</strong>.
    </p>
    <price-table :price-breakdown="priceBreakdown"></price-table>
    <p class="alert alert-danger" v-if="priceBreakdown.cost.remainingCredit < 0">
      Minimum purchase amount is $5.00. Should you go ahead with this
      transaction, you will be debited
      {{ Math.abs(priceBreakdown.cost.remainingCredit) | currency_round }} from
      your credits and will not be able to make subsequent bookings until this
      balance has been paid.
      <br/><br/>
      You can <a data-dismiss="modal" v-link="{path: '/billing'}">click here</a>
       to add credits to your account.
    </p>
    <div class="alert alert-warning">
      <p>Please note additional charges may be applied at the completion of your
      booking:</p>
      <ul class="list-margin">
        <li>Each kilometre travelled is billed at ${{ object.rate.perKmCost }}/km.</li>
        <li>{{ object.rate.includeKms }}km is only included when a booking uses the daily rate.</li>
        <li>{{ object.rate.includeKms }}km is included for each daily rate charged to your booking.</li>
        <li>Should you return the car late, every 15 min over your booking end time will incur an additional hour charged.</li>
        <li>Any tolls used within the month, will be billed to the reservation the following month.</li>
      </ul>
      <p>For more information regarding charges, please refer to the <a :href="$root.muaUrl" target="_blank">Member User Agreement</a></p>
    </div>
    <p class="callout callout-warning" v-if="!priceBreakdown.reservation.startDate && preAuth > 0">
      A <strong>{{ preAuth | currency_round }} pre-authorisation</strong> is required to cover
      the costs of any fees that may occur at the completion of your booking.
      This amount will be released and made available to your account within
      7 days, depending on when your booking is finalised/completed. If you
      have any questions regarding the pre-authorisation payment please feel
      free to contact GreenShareCar on 1300 575 878.
    </p>
    <template v-if="object.allowsFinanceCodes && !object.reservation.FinanceCode">
      <h4>Select a Payment Method</h4>
      <button class="btn btn-primary" type="button" @click="changePayment('financeCode')" :class="{ 'active btn-success': paymentMethod === 'financeCode' }">
        <span class="fa fa-id-card"></span> &nbsp;Finance Code
      </button>
      <button class="btn btn-primary" type="button" @click="changePayment('creditCard')" :class="{ 'active btn-success': paymentMethod === 'creditCard' }">
        <span class="fa fa-credit-card"></span> &nbsp;Credit Card
      </button>
      <template v-if="paymentMethod === 'financeCode'">
        <h4>Enter a Finance Code:</h4>
        <object-select-input :field.sync="object.financeCode"></object-select-input>
      </template>
      <template v-if="paymentMethod === 'creditCard' && object.cards">
        <h4>Select Card:</h4>
        <template v-if="object.cards.objects">
          <object-select-input :field.sync="object.cards"></object-select-input>
        </template>
      </template>
    </template>
    <template v-if="object.reservation.FinanceCode">
      <div class="alert alert-success">
        <span class="fa fa-check fa-2x fa-pull-right fa-fw"></span>
        <h4>Finance Code in Use:</h4>
        <p><strong>{{ object.reservation.FinanceCode.code }}</strong><br/>
        {{ object.reservation.FinanceCode.description }}</p>
      </div>
    </template>
    <template v-if="!object.allowsFinanceCodes && !object.reservation.FinanceCode">
      <h4>Select Card:</h4>
      <template v-if="object.cards.objects">
        <object-select-input :field.sync="object.cards"></object-select-input>
      </template>
    </template>
    <br/>
    <template v-if="priceBreakdown.businessReservation">
      <div class="alert alert-info">
        By selecting a business credit card, this booking is a business
        booking. All charges will be made to the account owner's business
        credit card. Account credits can not be used to pay for this booking.
      </div>
    </template>
    <div class="alert alert-info">
      Credit Card Payments are processed by
      <a href="https://stripe.com/au/">Stripe</a>. You may view their
      <a href="https://stripe.com/au/privacy/">Privacy Policy here.</a>
    </div>
  </div>
</template>


<script>
import moment from 'moment-timezone'
import ObjectSelectInput from '../inputs/ObjectSelectInput.vue'
import TextInput from '../inputs/TextInput.vue'
import PriceTable from '../displays/PriceTable.vue'

export default {
  name: 'PriceBreakdown',
  props: {
    object: {
      type: Object
    }
  },
  components: {
    ObjectSelectInput,
    PriceTable,
    TextInput
  },
  data () {
    return {
      hasOvernight: false,
      hasWeekend: false,
      weekendRateStart: null,
      weekendRateEnd: null,
      overnightRateStart: null,
      overnightRateEnd: null,
      overnightRate: 0,
      weekendRate: 0,
      preAuth: 0,
      minimum: true,
      paymentMethod: false,
      priceBreakdown: {
        businessReservation: false,
        reservation: {
          quotedCost: 0,
          createdAt: null
        },
        carUuid: '',
        cards: {
          required: true,
          valid: false,
          dirty: true
        },
        financeCode: {
          name: 'financeCode',
          label: 'Finance Code',
          objects: [],
          objectName: 'dropdownLabel',
          value: '',
          valid: false,
          required: true,
          dirty: true
        },
        cost: {},
        extras: [],
        optionalExtras: [],
        rate: {},
        extraCredit: 0
      },
      checkedExtras: [],
      extras: []
    }
  },
  ready () {
    this.priceBreakdown = this.object
  },
  computed: {
    startTime: function () {
      return moment(this.priceBreakdown.cost.startTime).format('LLLL')
    },
    endTime: function () {
      return moment(this.priceBreakdown.cost.endTime).format('LLLL')
    },
    total: function () {
      return this.priceBreakdown.cost.totalCost - this.priceBreakdown.reservation.quotedCost
    }
  },
  events: {
    getReservationInfo () {
      let authType = this.$route.authType
      let params = {
        accountUuid: this.object.driverUuid,
        carUuid: this.object.carUuid,
        rateUuid: this.object.rate.uuid,
        startTime: this.object.cost.startTime,
        endTime: this.object.cost.endTime
      }
      this.$http.get(`${authType}/get-reservation-info`, {
        params
      }).then((response, status, request) => {
        this.preAuth = response.data.preAuth
        this.weekendRateStart = response.data.weekendRateStart
        this.weekendRateEnd = response.data.weekendRateEnd
        this.overnightRateStart = response.data.overnightRateStart
        this.overnightRateEnd = response.data.overnightRateEnd
        this.hasOvernight = response.data.hasOvernight
        this.hasWeekend = response.data.hasWeekend
        this.weekendRate = response.data.weekendRate
        this.overnightRate = response.data.overnightRate
      })
    }
  },
  methods: {
    updateExtras (extra, index, $event) {
      if (this.checkedExtras[index]) {
        this.extras.push(extra)
      } else {
        this.extras.splice(this.extras.indexOf(extra), 1)
      }
      this.priceBreakdown.extras = this.extras
      this.updateCost()
    },
    updateCost () {
      let carUuid = this.priceBreakdown.carUuid
      let accountUuid = this.priceBreakdown.driverUuid
      let authType = this.$route.authType
      let startDate = this.priceBreakdown.cost.startTime
      let endDate = this.priceBreakdown.cost.endTime
      let extras = this.priceBreakdown.extras
      let extraCredit = this.priceBreakdown.extraCredit
      let alreadyPaid = this.priceBreakdown.reservation.quotedCost
      let createdAt = this.priceBreakdown.reservation.createdAt
      let useCredits = true
      if (this.priceBreakdown.businessReservation) useCredits = false
      this.$http.post(`${authType}/update-cost`, {
        accountUuid,
        carUuid,
        startDate,
        endDate,
        extras,
        extraCredit,
        alreadyPaid,
        createdAt,
        useCredits,
        promo: this.priceBreakdown.promo.value
      }).then((response, status, request) => {
        this.$emit('getReservationInfo')
        this.priceBreakdown.cost = response.data.cost
        this.priceBreakdown.extras = response.data.extras
      })
    },
    changePayment (method) {
      this.paymentMethod = method
      if (method === 'creditCard') {
        this.object.financeCode.value = ''
      } else if (method === 'financeCode') {
        this.object.cards.value = ''
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// PriceBreakdown.vue?266e6544